import { create } from 'zustand';

/**
 * Filmansicht (PLAN.md §35): offen oder zu, für welche Figur (null: die ganze Geschichte), was rechts steht, Tempo und ob die
 * Widersprüche an jedem Bild stehen – der Browser merkt sich Tempo und Prüfen. Dazu je Geschichte die zuletzt bearbeitete Szene.
 */
const STORE_KEY = 'dispositio.film';
/** Sekunden je Bild beim Abspielen */
export const FILM_TEMPI = [3, 5, 8, 12] as const;
/** Rechts neben dem Bild: die Zusammenfassung, der Szenentext zum Überfliegen oder das Material (PLAN.md §36) */
export type FilmSide = 'summary' | 'text' | 'material';

interface Saved {
  tempo: number;
  check: boolean;
}
const DEFAULTS: Saved = { tempo: 5, check: false };

function load(): Saved {
  try {
    const s = JSON.parse(localStorage.getItem(STORE_KEY) ?? '{}') as Partial<Saved>;
    return {
      tempo: FILM_TEMPI.includes(s.tempo as (typeof FILM_TEMPI)[number]) ? s.tempo! : DEFAULTS.tempo,
      check: s.check === true,
    };
  } catch {
    return DEFAULTS;
  }
}

interface FilmView extends Saved {
  open: boolean;
  /** der Film einer Figur (Entität); null: die ganze Geschichte */
  figure: string | null;
  /** was rechts steht (bleibt von Bild zu Bild) */
  side: FilmSide;
  /** den Film öffnen – für eine Figur bzw. die ganze Geschichte */
  openFilm: (figure: string | null) => void;
  close: () => void;
  setFigure: (figure: string | null) => void;
  setSide: (side: FilmSide) => void;
  setTempo: (tempo: number) => void;
  setCheck: (check: boolean) => void;
}

export const useFilmView = create<FilmView>((set, get) => {
  const update = (patch: Partial<Saved>): void => {
    set(patch);
    try {
      const { tempo, check } = get();
      localStorage.setItem(STORE_KEY, JSON.stringify({ tempo, check }));
    } catch {
      // privates Fenster o. Ä.: dann eben nicht gemerkt
    }
  };
  return {
    ...load(),
    open: false,
    figure: null,
    side: 'summary',
    openFilm: (figure) => set({ open: true, figure }),
    close: () => set({ open: false }),
    setFigure: (figure) => set({ figure }),
    setSide: (side) => set({ side }),
    setTempo: (tempo) => update({ tempo }),
    setCheck: (check) => update({ check }),
  };
});

const editedKey = (projectId: string): string => `dispositio.lastScene.${projectId}`;

/** Die Szene, deren Text gerade gespeichert wurde, als zuletzt bearbeitete merken */
export function rememberEdited(projectId: string, sceneId: string): void {
  try {
    localStorage.setItem(editedKey(projectId), sceneId);
  } catch {
    // ohne Speicher: kein Wiedereinstieg
  }
}

/** Die zuletzt bearbeitete Szene einer Geschichte (null: keine bekannt) */
export function lastEdited(projectId: string): string | null {
  try {
    return localStorage.getItem(editedKey(projectId));
  } catch {
    return null;
  }
}
